/**
 * Sports coloring pages — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_coloring_themes`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const sportsColoringPagesPack: ToilePack = {
    slug: 'sports-coloring-pages',
    category: 'coloring',
    name: 'Sports coloring pages',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'football', label: 'Football kick', subject: 'a child footballer kicking a ball toward a goal, numbered shirt, shin pads and the net behind'},
        {key: 'basketball', label: 'Basketball', subject: 'a basketball player leaping for a dunk, the ball at the rim, the backboard and court lines below'},
        {key: 'gear', label: 'Sports gear', subject: 'a page of sports equipment, a football, a basketball, a tennis racket, a baseball glove, a hockey stick and a medal'},
    ],
    styles: [
        {key: 'easy', label: 'Easy', style: 'an easy cartoon style for young children, thick bold uniform outlines, big rounded shapes and a cheerful face', palette: 'black ink only', framing: 'the drawing large and centered with a wide white margin'},
        {key: 'action', label: 'Action', style: 'comic-book action inking, confident bold outlines, a dynamic angle and a few motion lines behind the movement', palette: 'black ink only', framing: 'the drawing filling most of the page with a white margin'},
        {key: 'pattern', label: 'Pattern', style: 'a pattern-filled style for older kids, clean outlines with each large shape divided into small geometric and zigzag patterns', palette: 'black ink only', framing: 'centered with a white margin'},
    ],
    directions: ['tennis', 'volleyball', 'swimming'],
    directionHint: 'try: tennis',
    seo: {
        title: 'Sports coloring pages: soccer kick, basketball, gear',
        metaDescription: 'Nine sports coloring pages: a soccer kick, a basketball dunk and a page of balls and gear, in easy, action and pattern styles.',
        h1: 'Sports coloring pages: soccer kick, basketball dunk, sports gear',
        tagline: 'A player mid-kick, a player mid-dunk and a page of balls and gear, to color.',
        intro: 'Sports coloring pages for children who play on a team and for sports days at school. The nine pages show a child footballer kicking toward a goal, a basketball player leaping for a dunk, and a page of sports gear. The styles are easy, action with motion lines, and pattern. Type a different sport or subject, and the same style carries it.',
        useCases: [
            'a coloring page for a sports day at school',
            "a page for a child's team or club",
            'a rainy-day activity after a match',
            'a gift page for a young athlete',
        ],
        faq: [
            {q: 'What is on the sports gear page?', a: 'A football, a basketball, a tennis racket, a baseball glove, a hockey stick and a medal, drawn together on one page.'},
            {q: 'What is the difference between the styles?', a: 'Easy uses thick outlines and big rounded shapes for young children. Action uses comic-book inking with motion lines, and pattern fills each large shape with small geometric and zigzag patterns.'},
            {q: 'Can I ask for a different sport?', a: 'Yes. A sport you type is a request; the page keeps the same style but may not match it exactly.'},
            {q: 'Are the pages in color?', a: 'No. Each page is a black line drawing on white paper, ready to print and color.'},
        ],
    },
}
